/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo, useRef, useState } from 'react'

import DataTable from '@/components/data-table'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { createDnsColumnDefs } from '@/pages/dns-score/dns-table-columns'
import { AgGridReact } from 'ag-grid-react'
import {
  CheckCircle,
  Circle,
  Clock,
  Download,
  EllipsisVertical,
  FileDown,
  FileUp,
  Globe,
  Info,
  Loader2,
  Play,
  Tag,
  XCircle,
} from 'lucide-react'

import { ColumnVisibility } from '@/components/data-table/column-visiblity'
import InitialPageVisitGuideModal from '@/components/page-visit-guide'
import BulkTestPopup from '@/components/popups/bulk-email-test'
import EmailTestOptions from '@/components/popups/email-test-options'
import { LabelsDropdown } from '@/components/popups/labels-dropdown'
import { FilterLabelsEspButton } from '@/components/ui/filter-labels-esp-button'
import { PublishLinksDialog } from '@/components/popups/publish-links-dialog'
import ScheduleEmailTest from '@/components/popups/schedule-email-test'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SearchInput } from '@/components/ui/search-input'
import { Skeleton } from '@/components/ui/skeleton'
import { useLabels } from '@/hooks/use-labels'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  useGetDnsRecordsWithStats,
  usePublishDomainReport,
  useUnPublishDomainReport,
} from '@/hooks/use-postmaster'
import { usePermission } from '@/hooks/use-premission'
import { useGetUsage } from '@/hooks/use-subscription'
import { useUsageLimitChecker } from '@/hooks/use-usage-limit-checker'
import ManageLabels from '@/pages/labels/manage-label'
import { useServices } from '@/services/hooks/useServices'
import { AuthProvider, espTypeDescription, LabelType } from '@/types/common'
import { AssignedLabel } from '@/types/label.types'
import { ClockUser } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { useDistinctEspTypes, useMailboxStats } from '@/hooks/use-mailbox'
import StyledTooltip from '../../../components/styled-tooltip'

export const Route = createFileRoute('/_private/domain-health/')({
  component: DNSManager,
})

// Coin Icon Component
function CoinIcon({ 
  available, 
  max 
}: { 
  available: number; 
  max: number 
}) {
  // Get color based on available tests percentage
  const getColor = () => {
    if (max === 0) return "#ef4444"; // red
    const percentage = (available / max) * 100;
    if (percentage <= 25) return "#ef4444"; // red
    if (percentage <= 50) return "#eab308"; // yellow
    if (percentage <= 75) return "#22c55e"; // green
    return "#22c55e"; // green
  };

  const color = getColor();

  return (
    <div className="relative h-6 w-6">
      <svg
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-full"
      >
        {/* Coin circle */}
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke={color}
          strokeWidth="2"
          fill={color}
          fillOpacity="0.2"
        />
        {/* Coin inner circle for depth */}
        <circle
          cx="12"
          cy="12"
          r="7"
          stroke={color}
          strokeWidth="1.5"
          fill="none"
        />
        {/* Dollar sign or coin symbol */}
        <text
          x="12"
          y="16"
          fontSize="10"
          fontWeight="bold"
          fill={color}
          textAnchor="middle"
          fontFamily="Arial, sans-serif"
        >
          $
        </text>
      </svg>
    </div>
  );
}

type ScoreData = {
  value: string | number
  score: number
}

type DnsScoreData = {
  domain: string
  timestamp: string
  A: string
  AAAA: string
  ANAME: string
  CERT: string
  CNAME: string
  DKIM: string
  DMARC: string
  DNSKEY: string
  DS: string
  HINFO: string
  KEY: string
  MX: string
  NAPTR: string
  NS: string
  PTR: string
  RRSIG: string
  SOA: string
  SPF: string
  SRV: string
  SSHFP: string
  TXT: string
  overallScore: number
  labels: AssignedLabel[]
  espType: string
}

interface DNSRecord {
  type: string
  status: 'valid' | 'invalid'
  records: any
}

export default function DNSManager() {
  const gridRef = useRef<AgGridReact | null>(null)
  const isMobile = useIsMobile()

  const [limit, setLimit] = useState(100)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [showManageLabels, setShowManageLabels] = useState(false)
  const [selectedRows, setSelectedRows] = useState<DnsScoreData[]>([])
  const [showBulkTestPopup, setShowBulkTestPopup] = useState(false)
  const [showScheduleEmailTest, setShowScheduleEmailTest] = useState(false)
  const [selectedLabels, setSelectedLabels] = useState<string[]>([])
  const [selectedEspTypes, setSelectedEspTypes] = useState<string[]>([])
  const [showLinksDialog, setShowLinksDialog] = useState(false)
  const [showEmailTestOptions, setShowEmailTestOptions] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const { data: mailboxStats, isLoading: isMailboxStatsLoading } =
    useMailboxStats()

  const navigate = useNavigate()
  const { hasPermission } = usePermission()
  const { postmasterService } = useServices()
  const { isUsageLimitReached } = useUsageLimitChecker()
  const { data: labels } = useLabels(1, 100, LabelType.DNS)
  const { data: usageData } = useGetUsage()
  const { data: distinctEspTypes } = useDistinctEspTypes()
  const distinctEspDescriptions = useMemo(
    () =>
      distinctEspTypes?.map((espType) => ({
        value: espType,
        label: espTypeDescription[espType as keyof typeof espTypeDescription],
        bgcolor: '#6b7280',
      })),
    [distinctEspTypes],
  )
  // const { data: domainsData, isLoading } = useGetDomainsWithLatestData({
  //   page: currentPage,
  //   limit: limit,
  //   search: searchInput,
  // });

  const {
    mutateAsync: publishDomainReport,
    data: publishedLinksData,
    isPending: isPublishing,
  } = usePublishDomainReport()
  const { mutateAsync: unPublishDomainReport, isPending: isUnPublishing } =
    useUnPublishDomainReport()

  const handleRowClick = (params: any) => {
    navigate({
      to: `/domain-health/${params.data.domain}/${params.data.espType}`,
    })
  }

  const { data, isLoading: isDnsRecordsLoading } = useGetDnsRecordsWithStats({
    page: currentPage,
    limit: limit,
    search: searchInput,
    labels: selectedLabels.length > 0 ? selectedLabels : undefined,
    selectedEspTypes:
      selectedEspTypes.length > 0 ? selectedEspTypes : undefined,
  })

  const onSelectionChanged = useCallback(() => {
    const selected = gridRef.current?.api.getSelectedRows() || []
    setSelectedRows(selected)
    console.log(selected)
  }, [])

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const responce = await postmasterService.exportDnsRecordsData()
      const blob = new Blob([responce as unknown as BlobPart], {
        type: 'text/csv',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'Domain-records.csv'
      a.click()

      window.cioanalytics?.track('email_health_test_exported', {})
    } catch (error) {
      toast.error('Failed to export domain records')
    } finally {
      setIsExporting(false)
    }
  }

  const handleRunEmailTest = () => {
    if (isUsageLimitReached('limit_healthTestsUsedThisMonth')) {
      toast.error(
        "You've reached the maximum limit of domain health tests for this month.",
      )
      return
    }

    setShowEmailTestOptions(true)
  }

  const handleRunBulkTest = (domains: string[]) => {
    setShowBulkTestPopup(true)
    setSelectedRows(domains.map((domain) => ({ domain }) as DnsScoreData))
  }

  const handlePublishDomain = async (type: 'publish' | 'unpublish') => {
    try {
      const domains = selectedRows.map((row) => row.domain)
      if (type === 'publish') {
        const response = await publishDomainReport({
          domains,
        })
        console.log(response)
        setShowLinksDialog(true)
      } else {
        const response = await unPublishDomainReport({
          domains,
        })
        console.log(response)
      }
      toast.success(
        `Domain report ${
          type === 'publish' ? 'published' : 'unpublished'
        } successfully`,
      )
      const eventName =
        type === 'publish'
          ? 'email_health_test_published_all'
          : 'email_health_test_unpublished_all'
      window.cioanalytics?.track(eventName, {
        count: domains.length,
        domains: domains,
      })
    } catch (error) {
      toast.error(
        `Failed to ${
          type === 'publish' ? 'publish' : 'unpublish'
        } domain report`,
      )
    }
  }

  const handleLabelsChange = (newLabels: string[]) => {
    setSelectedLabels(newLabels)
    setCurrentPage(1)
  }

  const handleEspTypesChange = (newEspTypes: string[]) => {
    setSelectedEspTypes(newEspTypes)
    setCurrentPage(1)
  }

  // const removeLabel = (labelToRemove: string) => {
  //   setSelectedLabels(
  //     selectedLabels.filter((label) => label !== labelToRemove)
  //   );
  // };

  const handleGenerateNewTestEmail = () => {
    try {
      if (isUsageLimitReached('limit_healthTestsUsedThisMonth')) {
        toast.error(
          'You have reached the limit of domain health tests for this month',
        )
        return
      }
    } catch (error) {
      toast.error('Failed to generate new test email')
    }
  }

  const colDefs = useMemo(() => {
    const columns = createDnsColumnDefs({
      pageMetadata: {
        page: currentPage,
        limit: limit,
        search: searchInput,
        selectedLabels: selectedLabels,
        selectedEspTypes: selectedEspTypes,
      },
    })

    columns.forEach((column) => {
      if (column.colId === 'lastChecked' || column.colId === 'overallScore') {
        column.pinned = isMobile ? null : 'right'
      }
    })

    return columns
  }, [selectedLabels, limit, currentPage, searchInput, isMobile])

  return (
    <div className="flex flex-col">
      <InitialPageVisitGuideModal type="emailHealth" />
      {/* Info Banner */}

      <InfoMessage />
      {/* Navigation Header */}
      <main className="space-y-4 mt-4">
        <div className="flex flex-col lg:flex-row justify-between lg:items-center gap-2 mb-4">
          <div>
            <h1 className="page-heading">Domain Health Tests</h1>
            <p className="text-muted-foreground text-sm lg:text-base">
              Get real-time diagnostics to assess your email setup and avoid
              spam or delivery issues.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CoinIcon
              available={Math.max(
                (usageData?.maxHealthTestsUsedThisMonth || 0) -
                  (usageData?.healthTestsUsedThisMonth || 0),
                0,
              )}
              max={usageData?.maxHealthTestsUsedThisMonth || 0}
            />
            <div className="text-sm font-medium">
              Available Domain Health Tests:
            </div>
            <div className="text-sm text-muted-foreground">
              {Math.max(
                (usageData?.maxHealthTestsUsedThisMonth || 0) -
                  (usageData?.healthTestsUsedThisMonth || 0),
                0,
              )}
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:gap-4 mb-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 w-full">
          {/* Enhanced Domain Statistics Card */}
          <StyledTooltip description="Overview of your domain infrastructure. Shows the total number of unique domains connected to your system and the total number of domains that have been tested for doamin health.">
            <Card className="relative overflow-hidden border-l-4 border-l-blue-500 bg-gradient-to-br from-blue-50 dark:from-blue-900/20 to-blue-100/50 dark:to-blue-900/20 hover:shadow-lg transition-all duration-200 group w-full h-full">
              <CardHeader className="pb-2 p-3 sm:p-4">
                <CardTitle className="text-sm font-medium flex items-center space-x-2">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-200">
                    <Globe className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <span className="text-blue-900 dark:text-blue-400 text-xs sm:text-sm">
                    Domain Statistics
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0 space-y-2 sm:space-y-3">
                {/* Unique Connected Domains Section */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 p-2 sm:p-3 bg-white/50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                  <div className="flex items-baseline space-x-2">
                    {isMailboxStatsLoading ? (
                      <Skeleton className="rounded-lg size-[28px] sm:size-[32px] bg-blue-900/20" />
                    ) : (
                      <span className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-400">
                        {mailboxStats?.totalDomains || 0}
                      </span>
                    )}
                    <span className="text-xs sm:text-sm text-blue-700/70 dark:text-blue-400">
                      Domains
                    </span>
                  </div>
                  <div className="bg-gradient-to-r from-blue-400 to-blue-500 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full shadow-sm self-start sm:self-auto">
                    <span className="text-xs font-semibold text-white">
                      Unique
                    </span>
                  </div>
                </div>

                {/* Total Tested Domains Section */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 p-2 sm:p-3 bg-white/50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800">
                  <div className="flex items-baseline space-x-2">
                    {isDnsRecordsLoading ? (
                      <Skeleton className="rounded-lg size-[28px] sm:size-[32px] bg-blue-900/20" />
                    ) : (
                      <span className="text-xl sm:text-2xl font-bold text-blue-900 dark:text-blue-400">
                        {data?.stats.total || 0}
                      </span>
                    )}
                    <span className="text-xs sm:text-sm text-blue-700/70 dark:text-blue-400">
                      Domains
                    </span>
                  </div>
                  <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full shadow-sm self-start sm:self-auto">
                    <span className="text-xs font-semibold text-white">
                      Tested
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </StyledTooltip>

          {/* Enhanced Excellent Card */}
          <StyledTooltip description="Emails with excellent deliverability scores (>90). These domains have optimal DNS configuration, strong sender reputation, and minimal delivery issues.">
            <Card className="relative overflow-hidden border-l-4 border-l-green-500 bg-gradient-to-br from-green-50 to-emerald-100/50 dark:from-green-900/20 dark:to-emerald-900/20 hover:shadow-lg transition-all duration-200 group w-full h-full">
              <CardHeader className="pb-2 p-3 sm:p-4">
                <CardTitle className="text-sm font-medium flex items-center space-x-2">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-200">
                    <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                  </div>
                  <span className="text-green-900 dark:text-green-400 text-xs sm:text-sm">
                    Excellent
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                <div className="flex items-baseline space-x-2 mb-2 sm:mb-3">
                  {isDnsRecordsLoading ? (
                    <Skeleton className="rounded-lg size-[32px] sm:size-[36px] bg-green-900/20 dark:bg-green-400/20" />
                  ) : (
                    <span className="text-2xl sm:text-3xl font-bold text-green-900 dark:text-green-400">
                      {data?.stats.excellent || 0}
                    </span>
                  )}
                  <span className="text-xs sm:text-sm text-green-700/70 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-md">
                    Score &gt; 90
                  </span>
                </div>
                <div className="space-y-1.5 sm:space-y-2">
                  <p className="text-xs text-green-700/80 dark:text-green-300">
                    Your emails are reaching inboxes successfully with optimal
                    deliverability rates.
                  </p>
                  <div className="flex items-center text-xs text-green-600/70 dark:text-green-400/70">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    <span>Strong sender reputation</span>
                  </div>
                  <div className="flex items-center text-xs text-green-600/70 dark:text-green-400/70">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    <span>Proper authentication setup</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </StyledTooltip>

          {/* Enhanced Good Card */}
          <StyledTooltip description="Emails with good deliverability scores (80-90). These domains have minor configuration issues but generally good delivery performance with room for optimization.">
            <Card className="relative overflow-hidden border-l-4 border-l-yellow-500 bg-gradient-to-br from-yellow-50 via-yellow-50 to-amber-100/50 dark:from-yellow-900/20 dark:via-yellow-900/20 dark:to-amber-900/20 hover:shadow-lg transition-all duration-200 group w-full h-full">
              <CardHeader className="pb-2 p-4">
                <CardTitle className="text-sm font-medium flex items-center space-x-2">
                  <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-200">
                    <Circle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-yellow-900 dark:text-yellow-400">
                    Good
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex items-baseline space-x-2 mb-3">
                  {isDnsRecordsLoading ? (
                    <Skeleton className="rounded-lg size-[36px] bg-yellow-900/20 dark:bg-yellow-400/20" />
                  ) : (
                    <span className="text-3xl font-bold text-yellow-900 dark:text-yellow-400">
                      {data?.stats.good || 0}
                    </span>
                  )}
                  <span className="text-sm text-yellow-700/70 dark:text-yellow-400 bg-yellow-100 dark:bg-yellow-900/30 px-2 py-1 rounded-md">
                    Score 80-90
                  </span>
                </div>
                <div className="space-y-2">
                  <p className="text-xs text-yellow-700/80 dark:text-yellow-300">
                    Good deliverability with room for improvement in some areas.
                  </p>
                  <div className="flex items-center text-xs text-yellow-600/70 dark:text-yellow-400/70">
                    <Circle className="w-3 h-3 mr-1" />
                    <span>Minor configuration issues</span>
                  </div>
                  <div className="flex items-center text-xs text-yellow-600/70 dark:text-yellow-400/70">
                    <Circle className="w-3 h-3 mr-1" />
                    <span>Consider DNS optimization</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </StyledTooltip>

          {/* Enhanced Average Card */}
          <StyledTooltip description="Emails with average deliverability scores (70-80). These domains have moderate issues that may affect inbox placement and require attention to improve performance.">
            <Card className="relative overflow-hidden border-l-4 border-l-violet-500 bg-gradient-to-br from-violet-50 via-purple-50 to-fuchsia-100/50 dark:from-violet-900/20 dark:via-purple-900/20 dark:to-fuchsia-900/20 hover:shadow-lg transition-all duration-200 group w-full h-full">
              <CardHeader className="pb-2 p-4">
                <CardTitle className="text-sm font-medium flex items-center space-x-2">
                  <div className="w-8 h-8 bg-violet-500 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-200">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-violet-900 dark:text-violet-400">
                    Average
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex items-baseline space-x-2 mb-3">
                  {isDnsRecordsLoading ? (
                    <Skeleton className="rounded-lg size-[36px] bg-violet-900/20 dark:bg-violet-400/20" />
                  ) : (
                    <span className="text-3xl font-bold text-violet-900 dark:text-violet-400">
                      {data?.stats.average || 0}
                    </span>
                  )}
                  <span className="text-sm text-violet-700/70 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/30 px-2 py-1 rounded-md">
                    Score 70-80
                  </span>
                </div>
                <div className="space-y-2">
                  <p className="text-xs text-violet-700/80 dark:text-violet-300">
                    Moderate deliverability issues that need attention.
                  </p>
                  <div className="flex items-center text-xs text-violet-600/70 dark:text-violet-400/70">
                    <Clock className="w-3 h-3 mr-1" />
                    <span>Review DNS records</span>
                  </div>
                  <div className="flex items-center text-xs text-violet-600/70 dark:text-violet-400/70">
                    <Clock className="w-3 h-3 mr-1" />
                    <span>Check authentication</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </StyledTooltip>

          {/* Enhanced Poor Card */}
          <StyledTooltip description="Emails with poor deliverability scores (<70). These domains have critical issues that significantly impact inbox placement and require immediate attention to resolve.">
            <Card className="relative overflow-hidden border-l-4 border-l-red-500 bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-900/20 dark:to-red-900/20 hover:shadow-lg transition-all duration-200 group w-full h-full">
              <CardHeader className="pb-2 p-4">
                <CardTitle className="text-sm font-medium flex items-center space-x-2">
                  <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform duration-200">
                    <XCircle className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-red-900 dark:text-red-400">Poor</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="flex items-baseline space-x-2 mb-3">
                  {isDnsRecordsLoading ? (
                    <Skeleton className="rounded-lg size-[36px] bg-red-900/20" />
                  ) : (
                    <span className="text-3xl font-bold text-red-900 dark:text-red-400">
                      {data?.stats.poor || 0}
                    </span>
                  )}
                  <span className="text-sm text-red-700/70 dark:text-red-400 bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded-md">
                    Score &lt; 70
                  </span>
                </div>
                <div className="space-y-2">
                  <p className="text-xs text-red-700/80 dark:text-red-300">
                    Critical deliverability issues requiring immediate action.
                  </p>
                  <div className="flex items-center text-xs text-red-600/70 dark:text-red-400/70">
                    <XCircle className="w-3 h-3 mr-1" />
                    <span>Fix DNS configuration</span>
                  </div>
                  <div className="flex items-center text-xs text-red-600/70 dark:text-red-400/70">
                    <XCircle className="w-3 h-3 mr-1" />
                    <span>Review blacklist status</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </StyledTooltip>
        </div>

        
  

        {/* <div className="flex justify-end gap-2 mb-2">
          <div className="text-sm font-medium">
            Available Email Health Tests:
          </div>
          <div className="text-sm text-muted-foreground">56 / 73</div>
        </div> */}

        {/* Search and Controls */}

        <div className="flex flex-col lg:flex-row gap-2 items-center justify-between">
          <div className="flex items-center justify-between md:justify-start w-full gap-2">
            <SearchInput
              onSearch={(searchTerm) => {
                setSearchInput(searchTerm)
                if (
                  (searchTerm.length > 0 && searchInput.length === 0) ||
                  (searchTerm.length === 0 && searchInput.length > 0) ||
                  searchTerm !== searchInput
                ) {
                  setCurrentPage(1)
                }
              }}
              placeholder="Search by domains..."
              className="max-w-none sm:w-full md:max-w-[300px]"
            />
          </div>
          <div className="flex items-center gap-2 w-full lg:w-auto justify-end flex-wrap md:flex-nowrap">
            {selectedRows.length === 0 && (
              <>
                {hasPermission('email_health:run_test') && (
                  <Button
                    className=""
                    variant="default"
                    onClick={handleRunEmailTest}
                  >
                    <Play className="w-4 h-4" />
                    <span>Run Domain Health Test</span>
                  </Button>
                )}
              </>
            )}

            {selectedRows.length > 0 && (
              <>
                <div className="text-sm text-muted-foreground flex items-center gap-2 px-2 whitespace-nowrap">
                  <Badge variant="default">{selectedRows.length}</Badge>
                  <span>rows selected</span>
                </div>
                <Button
                  onClick={() => {
                    setSelectedRows([])
                    gridRef.current?.api.deselectAll()
                  }}
                  className="flex items-center gap-2"
                >
                  Deselect
                </Button>
              </>
            )}

            {hasPermission('email_health:run_test') && (
              <Button
                variant="outline"
                onClick={() => {
                  if (isUsageLimitReached('limit_healthTestsUsedThisMonth')) {
                    toast.error(
                      "You've reached the maximum limit of Domain health tests for this month.",
                    )
                    return
                  }

                  handleRunBulkTest(selectedRows.map((row) => row.domain))
                }}
              >
                <Play className="w-4 h-4" />
                Run Bulk Domain Health Test
              </Button>
            )}

            <FilterLabelsEspButton
              labels={
                labels?.data?.map((label) => ({
                  value: label._id,
                  label: label.name,
                  bgcolor: label.color || '',
                })) || []
              }
              selectedLabels={selectedLabels}
              onLabelsChange={handleLabelsChange}
              espTypes={distinctEspDescriptions || []}
              selectedEspTypes={selectedEspTypes}
              onEspTypesChange={handleEspTypesChange}
            />

            <div className="flex items-center gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline">
                    <EllipsisVertical className="h-4 w-4" />
                    Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {hasPermission('email_health:manage_schedule') && (
                    <DropdownMenuItem
                      onSelect={() => setShowScheduleEmailTest(true)}
                    >
                      <ClockUser className="w-4 h-4" />
                      Schedule Test
                    </DropdownMenuItem>
                  )}

                  <DropdownMenuItem
                    onSelect={() => handleExport()}
                    disabled={data?.records?.length === 0 || isExporting}
                  >
                    {isExporting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Exporting...</span>
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        <span>Export</span>
                      </>
                    )}
                  </DropdownMenuItem>

                  {hasPermission('mailboxes:edit') && (
                    <>
                      <DropdownMenuItem
                        onSelect={() => handlePublishDomain('publish')}
                        disabled={isPublishing || selectedRows.length === 0}
                      >
                        {isPublishing ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Publishing...</span>
                          </>
                        ) : (
                          <>
                            <FileUp className="h-4 w-4" />
                            Publish All
                          </>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => handlePublishDomain('unpublish')}
                        disabled={isUnPublishing || selectedRows.length === 0}
                      >
                        {isUnPublishing ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Unpublishing...</span>
                          </>
                        ) : (
                          <>
                            <FileDown className="h-4 w-4" />
                            Unpublish All
                          </>
                        )}
                      </DropdownMenuItem>

                      <DropdownMenuItem
                        onSelect={() => setShowManageLabels(true)}
                        disabled={selectedRows.length === 0}
                      >
                        <Tag className="h-4 w-4" />
                        Manage Labels
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>

              <ColumnVisibility
                gridRef={gridRef}
                storageKey="email-health-grid-columns"
              />
            </div>
          </div>
        </div>

        {/* DNS Records Grid */}
        <DataTable
          ref={gridRef}
          rowData={data?.records ?? []}
          columnDefs={colDefs}
          height={550}
          showPagination={true}
          onSelectionChanged={onSelectionChanged}
          defaultColDef={{
            editable: false,
            resizable: true,
            minWidth: 130,
            suppressMovable: true,
          }}
          onRowClicked={handleRowClick}
          loading={isDnsRecordsLoading}
          paginationProps={{
            pageSize: limit,
            totalPages: Math.ceil((data?.pagination.total || 0) / limit),
            currentPage: currentPage,
            onPageChange: (page: number) => {
              setCurrentPage(page)
            },
            rowsPerPage: limit,
            onRowsPerPageChange: (limit: number) => {
              setLimit(limit)
            },
            totalEntries: data?.pagination.total || 0,
          }}
        />

        {/* <ManageLabelsPopup
          open={showManageLabels}
          onOpenChange={setShowManageLabels}
          selectedMailboxIds={selectedRows.map((row) => row.domain)}
          lableType={LabelType.DNS}
          onSuccess={() => {
            setSelectedRows([]);
            gridRef.current?.api.deselectAll();
          }}
        /> */}
        {showManageLabels && (
          <ManageLabels
            open={showManageLabels}
            onOpenChange={setShowManageLabels}
            selectedRows={selectedRows.map((row) => ({
              _id: row.domain,
              labels: row.labels || [],
            }))}
            labelType={LabelType.DNS}
            afterChange={() => {
              setSelectedRows([])
              gridRef.current?.api.deselectAll()
            }}
          />
        )}

        {hasPermission('email_health:run_test') && showBulkTestPopup && (
          <BulkTestPopup
            isOpen={showBulkTestPopup}
            onClose={() => setShowBulkTestPopup(false)}
            domains={selectedRows.map((d) => d.domain) || []}
          />
        )}

        {hasPermission('email_health:manage_schedule') &&
          showScheduleEmailTest && (
            <ScheduleEmailTest
              isOpen={showScheduleEmailTest}
              onClose={() => setShowScheduleEmailTest(false)}
              type="email-health"
              initialSelectedDomains={selectedRows.map((d) => d.domain) || []}
            />
          )}

        {publishedLinksData && showLinksDialog && (
          <PublishLinksDialog
            open={showLinksDialog}
            onOpenChange={setShowLinksDialog}
            links={publishedLinksData}
            type="email-health"
          />
        )}

        {/* {showEmailHealthGuide && (
          <EmailHealthCheckGuide
            open={showEmailHealthGuide}
            onOpenChange={(open) => {
              setShowEmailHealthGuide(open);
              if (!open) {
                setShowEmailTestOptions(true);
              }
            }}
          />
        )} */}

        {showEmailTestOptions && (
          <EmailTestOptions
            isOpen={showEmailTestOptions}
            onClose={() => setShowEmailTestOptions(false)}
          />
        )}
      </main>
    </div>
  )
}

const InfoMessage = ({ className }: { className?: string }) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <Alert variant="info" className={className}>
      <Info className="h-4 w-4" />
      <AlertTitle>About Domain Health</AlertTitle>
      <AlertDescription>
        The Domain Health Check conducts hundreds of tests across your domain,
        email, and network performance to ensure all systems are operational and
        performing at their best. The resulting report provides insights into
        your domain's status, pinpointing crucial issues that require attention.
        {isOpen && (
          <span>
            <br />
            <br />
            Our Domain Health Checker also enables you to test email
            deliverability instantly. By clicking "Run Doamin health Test," you can
            analyze SPF, DKIM, and DMARC, among several other authentications
            checks, and receive a comprehensive deliverability score with
            actionable insights.
          </span>
        )}
        <button
          className="text-sm underline text-blue-700 px-2"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? 'Read Less' : 'Read More'}
        </button>
      </AlertDescription>
    </Alert>
  )
}
